import { rest } from '@0xtorch/core'
import { z } from 'zod'
import { createSha256Hash } from '../../utils/createSha256Hash'
import { fetchAllData } from '../../utils/fetchAllData'
import { binanceApiEndpoint } from '../constants'
import type { Deposit } from './schema'
import { depositSchema } from './schema'

const responseSchema = z.array(depositSchema)

type FetchAllDepositHistoriesParameters = {
  readonly apiKey: string
  readonly apiSecret: string
  readonly from: number
  readonly to: number
  readonly proxy?: string
}

const urlPath = '/sapi/v1/capital/deposit/hisrec'

export const fetchAllDepositHistories = ({
  apiKey,
  apiSecret,
  from,
  to,
  proxy,
}: FetchAllDepositHistoriesParameters): Promise<readonly Deposit[]> =>
  fetchAllData({
    from,
    to,
    limit: 1000,
    desc: true,
    idKey: 'id',
    timestampKey: 'insertTime',
    interval: 100,
    fetchData: async ({ from, to, limit, desc }) => {
      if (!desc) {
        throw new Error('desc must be true')
      }
      if (limit > 1000) {
        throw new Error('limit must be less than or equal to 1000')
      }

      const url = new URL(urlPath, binanceApiEndpoint)
      url.searchParams.append('status', '1')
      url.searchParams.append('timestamp', Date.now().toString())
      url.searchParams.append('limit', limit.toString())
      if (from !== undefined) {
        url.searchParams.append('startTime', from.toString())
      }
      if (to !== undefined) {
        url.searchParams.append('endTime', to.toString())
      }
      url.searchParams.append(
        'signature',
        await createSha256Hash(url.searchParams.toString(), apiSecret),
      )

      const response = await rest(
        proxy === undefined
          ? url.toString()
          : `${proxy}${encodeURIComponent(url.toString())}`,
        {
          fetchOptions: {
            method: 'GET',
            headers: {
              'X-MBX-APIKEY': apiKey,
            },
          },
          schema: responseSchema,
        },
      )

      return response
    },
  })
